import { AlertCircle, HelpCircle } from "lucide-react";
import {
  cloneElement,
  type FocusEvent,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  useRef,
  useState,
} from "react";

export type InputSize = "sm" | "md" | "lg";

export const SIZE_CLASSES: Record<InputSize, string> = {
  sm: "h-8 rounded-lg text-sm",
  md: "h-10 rounded-lg text-sm",
  lg: "h-12 rounded-xl text-sm",
};

export const PAD_X: Record<InputSize, { start: string; end: string }> = {
  sm: { start: "pl-2.5", end: "pr-2.5" },
  md: { start: "pl-3", end: "pr-3" },
  lg: { start: "pl-4", end: "pr-4" },
};

export const ICON_SIZE: Record<InputSize, number> = { sm: 12, md: 14, lg: 16 };

const ICON_INSET: Record<InputSize, string> = { sm: "left-2.5", md: "left-3", lg: "left-4" };
const ICON_PAD_START: Record<InputSize, string> = { sm: "pl-7", md: "pl-8", lg: "pl-10" };

export const ICON_INSET_END: Record<InputSize, string> = { sm: "right-2.5", md: "right-3", lg: "right-4" };
export const ICON_PAD_END: Record<InputSize, string> = { sm: "pr-7", md: "pr-8", lg: "pr-10" };

const STATUS_ICON_SIZE: Record<InputSize, number> = { sm: 10, md: 12, lg: 14 };

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "className"> {
  size?: InputSize;

  leadingIcon?: ReactElement<{ size?: number }>;

  trailingAction?: ReactNode;

  invalid?: boolean;

  helpText?: string;

  composer?: boolean;
}

export function Input({
  size = "lg",
  leadingIcon,
  trailingAction,
  invalid = false,
  helpText,
  composer = false,
  disabled,
  style,
  onPointerDown,
  onFocus,
  onBlur,
  ...rest
}: Readonly<InputProps>) {
  const isComposer = composer && !invalid;

  const hasTrailingElement = Boolean(trailingAction) || invalid || Boolean(helpText);

  const pointerDownRef = useRef(false);
  const [focusMethod, setFocusMethod] = useState<"pointer" | "keyboard" | null>(null);
  const isKeyboardFocus = focusMethod === "keyboard";
  const isFocused = focusMethod !== null;

  const handlePointerDown = (event: ReactPointerEvent<HTMLInputElement>) => {
    pointerDownRef.current = true;
    onPointerDown?.(event);
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    setFocusMethod(pointerDownRef.current ? "pointer" : "keyboard");
    pointerDownRef.current = false;
    onFocus?.(event);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    setFocusMethod(null);
    onBlur?.(event);
  };

  return (
    <div className="relative isolate w-full min-w-0 flex-1">
      {leadingIcon && (
        <span
          aria-hidden="true"
          className={`pointer-events-none absolute ${ICON_INSET[size]} top-1/2 z-10 inline-flex -translate-y-1/2 items-center justify-center text-content-tertiary`}
        >
          {cloneElement(leadingIcon, { size: ICON_SIZE[size] })}
        </span>
      )}
      <input
        disabled={disabled}
        aria-invalid={invalid || undefined}
        style={style}
        onPointerDown={handlePointerDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={[
          "block w-full truncate leading-none pb-0.5 text-content-primary placeholder:text-content-tertiary",
          SIZE_CLASSES[size],
          leadingIcon ? ICON_PAD_START[size] : PAD_X[size].start,
          trailingAction ? "pr-11" : hasTrailingElement ? ICON_PAD_END[size] : PAD_X[size].end,
          "outline-hidden transition-colors disabled:cursor-not-allowed disabled:opacity-50",
          isComposer
            ? [
                "border shadow-sm",
                isFocused
                  ? "border-brand-gradient-warm-vertical"
                  : "bg-surface-elevated border-border-default hover:border-border-strong",
                isKeyboardFocus ? "input-focus-ring" : "",
              ]
                .filter(Boolean)
                .join(" ")
            : [
                "bg-surface-elevated border",
                invalid ? "border-error" : isFocused ? "border-border-focus" : "border-border-input",

                isKeyboardFocus ? "input-focus-ring" : size === "lg" ? "shadow-[0_1px_1px_rgba(0,0,0,0.05)]" : "",
              ]
                .filter(Boolean)
                .join(" "),
        ].join(" ")}
        {...rest}
      />
      {trailingAction ? (
        <span className="absolute right-2 top-1/2 z-10 inline-flex -translate-y-1/2 items-center justify-center">
          {trailingAction}
        </span>
      ) : invalid ? (
        <span
          aria-hidden="true"
          className={`pointer-events-none absolute ${ICON_INSET_END[size]} top-1/2 z-10 inline-flex -translate-y-1/2 items-center justify-center text-error`}
        >
          <AlertCircle size={STATUS_ICON_SIZE[size]} />
        </span>
      ) : (
        helpText && (
          <span
            aria-hidden="true"
            className={`absolute ${ICON_INSET_END[size]} top-1/2 z-10 inline-flex -translate-y-1/2 items-center justify-center text-content-tertiary transition-colors hover:text-content-primary`}
          >
            <HelpCircle size={STATUS_ICON_SIZE[size]} />
          </span>
        )
      )}
    </div>
  );
}
